// /////////////////////////////////////////////////////////////
// [ファイル] CustomerAssetViewer.tsx
// [概要] 顧客の添付ファイル (PDF・画像) をパネル内に表示するビューア。
//        ターコイズ取引先詳細ダイアログの右側に置き、取引先情報と添付書類を
//        横並びで照合できるようにする (顧客詳細 → 添付プレビュー /ターコイズ詳細から起動)。
//        対象は PDF・画像のみ (それ以外はダイアログ内表示ができないため選択肢に出さない)。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/firebase';
import * as Icons from '../constants/icons';
import { CustomerAsset, previewKindOf, iconFor, formatBytes } from './CustomerAssetList';

interface Props {
  // 表示対象の添付 (呼び出し側で PDF・画像に絞って渡す)
  assets: CustomerAsset[];
  assetId: number;
  onSelect: (assetId: number) => void;
  // 横並び表示を終了する
  onClose: () => void;
}

const CustomerAssetViewer: React.FC<Props> = ({ assets, assetId, onSelect, onClose }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const index = assets.findIndex((a) => a.id === assetId);
  const asset = index >= 0 ? assets[index] : null;

  // 選択ファイルが変わるたびに Storage のダウンロード URL を取り直す
  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setError(null);
    if (!asset) return;
    getDownloadURL(ref(storage, asset.gcs_path))
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch((e) => {
        console.error('添付の取得エラー:', e);
        if (!cancelled) setError('ファイルを開けませんでした');
      });
    return () => {
      cancelled = true;
    };
  }, [asset]);

  const step = (delta: number) => {
    const next = assets[index + delta];
    if (next) onSelect(next.id);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      {/* ツールバー (ファイル切替・前後送り・別タブ・閉じる) */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75,
          borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper',
        }}
      >
        {asset && iconFor(asset.file_name)}
        <TextField
          select
          size="small"
          value={asset ? asset.id : ''}
          onChange={(e) => onSelect(Number(e.target.value))}
          sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.5 } }}
          SelectProps={{ MenuProps: { disableScrollLock: true } }}
        >
          {assets.map((a) => (
            <MenuItem key={a.id} value={a.id} sx={{ fontSize: '0.8rem' }}>
              {a.file_name}
              {a.label ? ` (${a.label})` : ''}
            </MenuItem>
          ))}
        </TextField>
        {assets.length > 1 && (
          <>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
              {index + 1} / {assets.length}
            </Typography>
            <Tooltip title="前のファイル">
              <span>
                <IconButton size="small" onClick={() => step(-1)} disabled={index <= 0} sx={{ p: 0.25 }}>
                  <Icons.ChevronLeft sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="次のファイル">
              <span>
                <IconButton size="small" onClick={() => step(1)} disabled={index >= assets.length - 1} sx={{ p: 0.25 }}>
                  <Icons.ChevronRight sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        <Tooltip title="別タブで開く">
          <span>
            <IconButton size="small" onClick={() => url && window.open(url, '_blank', 'noopener')} disabled={!url} sx={{ p: 0.25 }}>
              <Icons.OpenInNew sx={{ fontSize: 17 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="横並び表示を閉じる">
          <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
            <Icons.Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* 本体 (PDF は iframe、画像は img) */}
      <Box
        sx={{
          flex: 1, minHeight: 0, bgcolor: '#525659',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {!asset && (
          <Typography variant="body2" sx={{ color: '#fff' }}>表示できる添付ファイルがありません</Typography>
        )}
        {asset && !url && !error && <CircularProgress size={32} sx={{ color: '#fff' }} />}
        {error && <Typography variant="body2" sx={{ color: '#fff' }}>{error}</Typography>}
        {asset && url && (
          previewKindOf(asset) === 'pdf' ? (
            <Box component="iframe" src={url} title={asset.file_name} sx={{ width: '100%', height: '100%', border: 0 }} />
          ) : (
            <Box
              component="img"
              src={url}
              alt={asset.file_name}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          )
        )}
      </Box>

      {asset?.file_size ? (
        <Typography variant="caption" sx={{ px: 1.5, py: 0.5, color: 'text.disabled', fontSize: '0.7rem' }}>
          {formatBytes(asset.file_size)}
        </Typography>
      ) : null}
    </Box>
  );
};

export default CustomerAssetViewer;
